<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import type {
    Coverage,
    CoverageOption,
    FlowPlanCoverageProps,
  } from '@/entities/Flow/components/FlowPlanCoverage/FlowPlanCoverage.types'
  import { SchemeType } from '@/shared/types/api.types'
  import HeartPulseIcon from '@/shared/components/icons/HeartPulseIcon.vue'
  import CrossIcon from '@/shared/components/icons/CrossIcon.vue'
  import ToothIcon from '@/shared/components/icons/ToothIcon.vue'
  import SkiingIcon from '@/shared/components/icons/SkiingIcon.vue'
  import VirusIcon from '@/shared/components/icons/VirusIcon.vue'
  import LuggageIcon from '@/shared/components/icons/LuggageIcon.vue'
  import PassportIcon from '@/shared/components/icons/PassportIcon.vue'
  import BicycleIcon from '@/shared/components/icons/BicycleIcon.vue'
  import PlaneIcon from '@/shared/components/icons/PlaneIcon.vue'
  import ShieldUserIcon from '@/shared/components/icons/ShieldUserIcon.vue'
  import ScalesIcon from '@/shared/components/icons/ScalesIcon.vue'
  import ClockIcon from '@/shared/components/icons/ClockIcon.vue'
  import TooltipInsuranceValues from '@/shared/components/TooltipInsuranceValues/TooltipInsuranceValues.vue'

  const props = defineProps<FlowPlanCoverageProps>()

  const { t } = useI18n()

  const healthCoverage: CoverageOption[] = [
    {
      icon: HeartPulseIcon,
      name: t('outbound.select_plan.insurance_values.category_1.item_1.name'),
      price:
        props.plan.schemeTypeCode === SchemeType.FlyEurope
          ? t('outbound.select_plan.insurance_values.category_1.item_1.desc_flyeurope')
          : t('outbound.select_plan.insurance_values.category_1.item_1.desc'),
      tooltip: t('outbound.select_plan.insurance_values.category_1.item_1.tooltip_desc'),
    },
    {
      icon: ToothIcon,
      name: t('outbound.select_plan.insurance_values.category_1.item_2.name'),
      price: t('outbound.select_plan.insurance_values.category_1.item_2.desc'),
    },
    {
      icon: SkiingIcon,
      name: t('outbound.select_plan.insurance_values.category_1.item_3.name'),
      price: t('outbound.select_plan.insurance_values.addon'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      icon: VirusIcon,
      name: t('outbound.select_plan.insurance_values.category_1.item_4.name'),
      price: t('outbound.select_plan.insurance_values.addon'),
      notAvailableFor: [SchemeType.Regional, SchemeType.FlyEurope],
    },
  ]

  const belongingsCoverage: CoverageOption[] = [
    {
      icon: LuggageIcon,
      name: t('outbound.select_plan.insurance_values.category_2.item_1.name'),
      price: t('outbound.select_plan.insurance_values.category_2.item_1.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      icon: PassportIcon,
      name: t('outbound.select_plan.insurance_values.category_2.item_2.name'),
      price: t('outbound.select_plan.insurance_values.category_2.item_2.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      icon: BicycleIcon,
      name: t('outbound.select_plan.insurance_values.category_2.item_3.name'),
      price: t('outbound.select_plan.insurance_values.category_2.item_3.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
  ]

  const tripCoverage: CoverageOption[] = [
    {
      icon: PlaneIcon,
      name: t('outbound.select_plan.insurance_values.category_3.item_1.name'),
      price: t('outbound.select_plan.insurance_values.category_3.item_1.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      icon: ShieldUserIcon,
      name: t('outbound.select_plan.insurance_values.category_3.item_2.name'),
      price: t('outbound.select_plan.insurance_values.category_3.item_2.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      icon: ScalesIcon,
      name: t('outbound.select_plan.insurance_values.category_3.item_3.name'),
      price: t('outbound.select_plan.insurance_values.category_3.item_3.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      icon: ClockIcon,
      name: t('outbound.select_plan.insurance_values.category_3.item_4.name'),
      price: t('outbound.select_plan.insurance_values.category_3.item_4.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
    },
  ]

  const coverage: Coverage[] = [
    {
      label: t('outbound.select_plan.insurance_values.category_1.label'),
      options: healthCoverage,
    },
    {
      label: t('outbound.select_plan.insurance_values.category_2.label'),
      options: belongingsCoverage,
    },
    {
      label: t('outbound.select_plan.insurance_values.category_3.label'),
      options: tripCoverage,
    },
  ]
</script>

<template>
  <div class="coverage">
    <div v-for="{ label, options } in coverage" :key="label" class="coverage__block">
      <div
        :class="[
          'coverage__block-title',
          {
            'coverage__block-title--disabled': options.every((option) =>
              option.notAvailableFor?.includes(plan.schemeTypeCode)
            ),
          },
        ]"
      >
        {{ label }}
      </div>
      <div
        v-for="{ name, price, icon, notAvailableFor, tooltip } in options"
        :key="name"
        class="coverage__block-item"
      >
        <CrossIcon
          v-if="notAvailableFor?.includes(plan.schemeTypeCode)"
          :size="20"
          color="#526C82"
          :opacity="0.4"
        />
        <Component
          :is="icon"
          v-else
          :size="20"
          class="coverage__item-icon"
          color="#394B5ACC"
          :opacity="0.8"
        />
        <span
          :class="[
            'coverage__item-description',
            {
              'coverage__item-description--disabled': notAvailableFor?.includes(
                plan.schemeTypeCode
              ),
            },
          ]"
        >
          {{ name }}
          <TooltipInsuranceValues v-if="tooltip" :info="tooltip" />
        </span>
        <span v-if="!notAvailableFor?.includes(plan.schemeTypeCode)" class="coverage__item-price">
          {{ price }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @import '@/shared/assets/styles/mixins';

  .coverage {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;

    &__block-title {
      @include caption-4-plus-plus;

      display: flex;
      padding-bottom: 8px;
      align-items: center;
      gap: 10px;
      align-self: stretch;

      &--disabled {
        color: rgb(82 108 130 / 40%);
      }
    }

    &__block {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      align-self: stretch;
    }

    &__block-item {
      @include caption-6-plus;

      display: flex;
      padding: 4px 0;
      align-items: flex-start;
      gap: 12px;
      align-self: stretch;

      @include mobile-flow {
        gap: 12px;
      }
    }

    &__item-icon {
      margin-top: 2px;
    }

    &__item-description {
      flex: 1 0 0;

      &--disabled {
        color: rgb(82 108 130 / 40%);
        text-decoration-line: line-through;
      }

      @include mobile-flow {
        margin-inline-start: -4px;
        max-width: 44%;
      }
    }

    &__item-price {
      display: flex;
      align-items: flex-end;
      gap: 4px;

      @include mobile-flow {
        margin-inline-start: auto;
      }
    }
  }

  .rtl {
    .coverage {
      &__item-description {
        @include mobile-flow {
          &:nth-of-type(1) {
            max-width: 41%;
          }
        }
      }
    }
  }
</style>
